<template>
  <section class="loja" :aria-label="$t('clan.loja.titulo')">
    <p class="loja__rotulo">
      {{ $t('clan.loja.titulo') }}
      <span class="loja__pontos t-numero">
        <FishIcon nome="medal" tamanho="miudo" />
        {{ formato(loja.seusPontos) }}
      </span>
      <button v-if="isLeader" type="button" class="loja__alternar" @click="alternarModo">
        {{ modo === 'membro' ? $t('clan.loja.repor') : $t('clan.loja.verPrateleira') }}
      </button>
    </p>

    <!-- ===================== MODO MEMBRO: a prateleira ===================== -->
    <template v-if="modo === 'membro'">
      <!--
        O que FALTA vai no topo, e não como recusa depois do clique (D11). Era
        um par de travas; o tempo mínimo de clã saiu em 09/09/2026 e sobrou o
        teto diário, que volta amanhã.
      -->
      <p class="loja__restantes">
        {{ $t('clan.loja.restantes', { n: loja.comprasRestantesHoje }) }}
      </p>

      <EmptyState
        v-if="!loja.itens.length"
        icone="box"
        :titulo="$t('clan.loja.vazia')"
        :explicacao="isLeader ? $t('clan.loja.vaziaLider') : $t('clan.loja.vaziaMembro')"
      />

      <template v-else>
        <FishGrid :total="loja.itens.length" :colunas-telefone="4" :colunas-desktop="6">
          <FishSlot
            v-for="item in loja.itens"
            :key="item.itemId"
            :item="{ id: item.itemId, nome: item.nome, imagem: item.imagem || arteDe(item.nome) }"
            :quantidade="item.emEstoque"
            :selecionado="selecionado && selecionado.itemId === item.itemId"
            @selecionar="selecionado = item"
          />
        </FishGrid>

        <!-- O cartão de detalhe, como na loja do Velho João (D16). -->
        <div v-if="selecionado" class="loja__ficha">
          <p class="loja__ficha-nome">{{ selecionado.nome }}</p>
          <p v-if="selecionado.descricao" class="loja__ficha-texto">{{ selecionado.descricao }}</p>

          <!-- O número do item, quando ele tem um. Mesmo rótulo do painel da
               loja premium: é o mesmo atributo do mesmo item. -->
          <p v-if="selecionado.poderDePesca > 0" class="loja__ficha-linha">
            <span class="crescer">{{ $t('shop.panel.power') }}</span>
            <strong class="t-numero">{{ selecionado.poderDePesca }}</strong>
          </p>

          <!--
            O que o item FAZ, nas palavras do catálogo (`tooltip_text`). Era o
            tooltip do `ItemCard` antigo; a grade perdeu o hover e este texto
            sumiu junto. A loja premium o recuperou em 07/09/2026 e a barraca
            ficou para trás — o jogador via "Tônico Revitalizante, 191" sem saber
            quanta energia ele devolve, e decidia gastar no escuro.
          -->
          <ul v-if="linhasDaDica.length" class="loja__dica">
            <li v-for="(linha, i) in linhasDaDica" :key="i">{{ linha }}</li>
          </ul>

          <!--
            A tabela do baú, com a chance de cada prêmio — do servidor, do mesmo
            cálculo que o sorteio usa. É o que o jogador olha antes de gastar os
            pontos que levou semanas doando.
          -->
          <div v-if="selecionado.recompensas && selecionado.recompensas.length" class="loja__bau">
            <p class="loja__bau-titulo">{{ $t('shop.lootbox.possibleRewards') }}</p>
            <ul class="loja__premios">
              <li v-for="(r, i) in selecionado.recompensas" :key="i" class="loja__premio">
                <span class="crescer">{{ nomeDoPremio(r) }}</span>
                <span class="t-numero">{{ r.chance }}%</span>
              </li>
            </ul>
          </div>

          <p class="loja__ficha-linha">
            <span class="crescer">{{ $t('clan.loja.preco') }}</span>
            <strong class="t-numero">{{ formato(selecionado.preco) }}</strong>
          </p>
          <p v-if="selecionado.unidadesPorCompra > 1" class="loja__ficha-linha">
            <span class="crescer">{{ $t('clan.loja.entrega') }}</span>
            <strong class="t-numero">{{ selecionado.unidadesPorCompra }}×</strong>
          </p>

          <FishButton
            variante="primario"
            bloco
            som="compra"
            :desabilitado="!podeComprarOSelecionado"
            :motivo="motivoDeNaoComprar"
            :carregando="comprando"
            @click="comprar"
          >
            {{ $t('clan.loja.comprar') }}
          </FishButton>
        </div>
      </template>
    </template>

    <!-- ===================== MODO LÍDER: a reposição ===================== -->
    <template v-else>
      <p class="loja__caixa t-numero">
        {{ $t('clan.loja.caixa') }}: {{ formato(reposicao.caixa) }}
      </p>
      <p class="loja__nota">{{ $t('clan.loja.notaDoPreco') }}</p>

      <ul class="loja__catalogo">
        <li v-for="item in reposicao.itens" :key="item.id">
          <img class="loja__arte" :src="item.imagem || arteDe(item.nome)" :alt="item.nome" />
          <div class="crescer">
            <p class="loja__ficha-nome">{{ item.nome }}</p>
            <p class="loja__ficha-texto">
              {{ $t('clan.loja.saiPor', { pontos: formato(item.precoEmPontos) }) }}
            </p>
          </div>
          <div class="loja__repor">
            <input
              v-model.number="unidades[item.id]"
              type="number"
              min="1"
              max="10"
              class="loja__unidades t-numero"
              :aria-label="$t('clan.loja.unidades')"
            />
            <FishButton
              tamanho="pequeno"
              som="compra"
              :desabilitado="custoDe(item) > reposicao.caixa || repondo === item.id"
              :motivo="custoDe(item) > reposicao.caixa ? $t('clan.loja.caixaCurto') : ''"
              :carregando="repondo === item.id"
              @click="repor(item)"
            >
              {{ formato(custoDe(item)) }}
            </FishButton>
          </div>
        </li>
      </ul>
    </template>

    <p v-if="erro" class="loja__erro">{{ erro }}</p>
  </section>
</template>

<script>
import { FishButton, FishGrid, FishIcon, FishSlot, EmptyState } from '@/shared/ui';
import { getItemImage } from '@/shared/utils/itemImages';
import * as v2 from '@/core/services/api/v2';

/**
 * A LOJA DO CLÃ (D24) — a barraca no cais.
 *
 * É a proposta que resolve sozinha o problema nº 1 do D22, "a tela não vende o
 * clã": a resposta a "por que eu entraria num clã" deixa de ser "+10% de ouro" e
 * passa a ser **item premium sem pagar**.
 *
 * Seção do cais, e não modal nem rota própria — o D24 é explícito: "fica como
 * uma barraca no cais do clã". Uma rota separada faria a loja parecer outro
 * lugar, e o ponto é que ela é do clã.
 *
 * **Dois modos, uma barraca.** O membro vê a prateleira; o líder alterna para a
 * reposição. Duas telas dariam a impressão de duas lojas, e o líder também
 * compra na dele.
 *
 * O preço em pontos é do JOGO e não do líder (D24): a escolha dele é *o que*
 * estocar, e só. Por isso o modo líder mostra o preço como informação e o custo
 * como botão — o que ele decide é gastar o caixa, não precificar.
 */
export default {
  name: 'ClanLoja',
  components: { FishButton, FishGrid, FishIcon, FishSlot, EmptyState },
  props: {
    isLeader: { type: Boolean, default: false },
  },
  emits: ['erro', 'comprou'],
  data() {
    return {
      modo: 'membro',
      loja: { itens: [], seusPontos: 0, comprasRestantesHoje: 0 },
      reposicao: { caixa: 0, itens: [] },
      unidades: {},
      selecionado: null,
      comprando: false,
      repondo: null,
      erro: '',
    };
  },
  computed: {
    podeComprarOSelecionado() {
      if (!this.selecionado || this.comprando) return false;
      if (this.loja.comprasRestantesHoje <= 0) return false;
      if (this.selecionado.emEstoque < 1) return false;
      return this.loja.seusPontos >= this.selecionado.preco;
    },
    /**
     * O `tooltip_text` do catálogo, quebrado por `|` — era assim que o Strapi
     * separava linhas, e a loja premium continua escrevendo desse jeito.
     *
     * **Some no baú**, pela mesma razão que soma lá: o texto dele é uma tabela de
     * chances digitada à mão na época do Strapi ("50% Minhoca | 30% Moedas") e
     * ela não bate com o sorteio de verdade. Com as duas na tela o jogador lê
     * números diferentes para a mesma coisa, um deles falso.
     */
    linhasDaDica() {
      if (this.selecionado?.recompensas?.length) return [];
      return String(this.selecionado?.tooltip || '')
        .split('|')
        .map((l) => l.trim())
        .filter(Boolean);
    },
    /** O que falta, escrito NO BOTÃO (D11) — nunca um botão morto sem motivo. */
    motivoDeNaoComprar() {
      if (!this.selecionado || this.podeComprarOSelecionado) return '';
      if (this.loja.comprasRestantesHoje <= 0) return this.$t('clan.loja.travaDiaria');
      if (this.selecionado.emEstoque < 1) return this.$t('clan.loja.semEstoque');
      return this.$t('clan.loja.semPontos');
    },
  },
  created() {
    void this.carregar();
  },
  methods: {
    async carregar() {
      try {
        this.loja = await v2.lojaDoCla(this.$store.state.player.id);
        // O item selecionado é reapontado para a linha nova: guardar o objeto
        // antigo deixaria o estoque congelado no cartão depois de uma compra.
        if (this.selecionado) {
          this.selecionado =
            this.loja.itens.find((i) => i.itemId === this.selecionado.itemId) ?? null;
        }
      } catch (error) {
        if (error?.response?.status !== 404) this.$emit('erro', error);
      }
    },

    async carregarReposicao() {
      try {
        this.reposicao = await v2.reposicaoDaLoja(this.$store.state.player.id);
        for (const item of this.reposicao.itens) {
          if (!this.unidades[item.id]) this.unidades[item.id] = 1;
        }
      } catch (error) {
        this.$emit('erro', error);
      }
    },

    async alternarModo() {
      this.erro = '';
      this.modo = this.modo === 'membro' ? 'lider' : 'membro';
      if (this.modo === 'lider') await this.carregarReposicao();
      else await this.carregar();
    },

    arteDe(nome) {
      return getItemImage(nome);
    },

    /**
     * "3 Minhoca", "500–1.500 Ouro". A quantidade vem do servidor como número ou
     * como faixa `{min, max}`, e sem ela a tabela do baú diria só o que cai, não
     * quanto — que é metade do que se quer saber antes de gastar.
     */
    nomeDoPremio(r) {
      const nome = r.tipo === 'gold' ? this.$t('shop.panel.rewardGold') : r.nome || '';
      const q = r.quantidade;
      if (q && typeof q === 'object') return `${q.min}–${q.max} ${nome}`;
      return q && q !== 1 ? `${q} ${nome}` : nome;
    },

    formato(valor) {
      return new Intl.NumberFormat('pt-BR').format(valor || 0);
    },

    /** O custo em caixa das unidades escolhidas. Do servidor, por unidade. */
    custoDe(item) {
      return item.custoPorUnidade * (this.unidades[item.id] || 1);
    },

    async comprar() {
      this.comprando = true;
      this.erro = '';
      try {
        await v2.comprarNaLojaDoCla(this.$store.state.player.id, this.selecionado.itemId);
        await this.carregar();
        // O saldo de moedas do HUD é do jogador, e a loja acabou de gastá-lo.
        this.$emit('comprou');
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t('clan.loja.falhou');
      } finally {
        this.comprando = false;
      }
    },

    async repor(item) {
      this.repondo = item.id;
      this.erro = '';
      try {
        await v2.reporNaLoja(
          this.$store.state.player.id,
          item.id,
          Math.min(Math.max(this.unidades[item.id] || 1, 1), 10),
        );
        await this.carregarReposicao();
      } catch (error) {
        this.erro = error?.response?.data?.message || this.$t('clan.loja.falhou');
      } finally {
        this.repondo = null;
      }
    },
  },
};
</script>

<style scoped>
.loja {
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.loja__rotulo {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

/* Os pontos ficam no rótulo da barraca, e não num HUD à parte: é a moeda desta
 * loja, e ela só existe aqui. */
.loja__pontos {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
  color: var(--latao);
}

.loja__alternar {
  margin-left: auto;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: var(--f-1);
  color: var(--latao);
  text-decoration: underline dashed;
  cursor: pointer;
}

.loja__restantes,
.loja__nota,
.loja__caixa {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.loja__caixa {
  font-size: var(--f-2);
  color: var(--tinta);
}

.loja__ficha {
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-2);
  display: flex;
  flex-direction: column;
  gap: var(--e-1);
}

.loja__ficha-nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  color: var(--tinta);
}

.loja__ficha-texto {
  margin: 0;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.loja__ficha-linha {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  margin: 0;
  font-size: var(--f-2);
  color: var(--tinta);
}

/* --------------------------------------------------------- modo líder */

.loja__catalogo {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

.loja__catalogo li {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  border-bottom: 1px dashed var(--papel-sombra);
  padding-bottom: var(--e-2);
}

.loja__arte {
  width: 48px;
  height: 48px;
  object-fit: contain;
  flex: 0 0 auto;
}

.loja__repor {
  display: flex;
  align-items: center;
  gap: var(--e-1);
}

.loja__unidades {
  width: 52px;
  padding: var(--e-1);
  border: 2px solid var(--madeira);
  background: var(--papel);
  color: var(--tinta);
  font: inherit;
  text-align: center;
}

/* A dica e a tabela do baú, no idioma do caderno: fio tracejado separando, sem
 * caixa nova — papel sobre papel seria uma superfície a mais por nada. */
.loja__dica {
  list-style: none;
  margin: 0;
  padding: 0 0 0 var(--e-2);
  border-left: 3px solid var(--latao);
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.loja__bau {
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-1);
}

.loja__bau-titulo {
  margin: 0 0 var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta);
}

.loja__premios {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.loja__premio {
  display: flex;
  align-items: baseline;
  gap: var(--e-2);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.loja__erro {
  margin: 0;
  font-size: var(--f-1);
  color: var(--ruim, #a33);
}
</style>
